import { ChangeEvent, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FlexStack, Icon } from '~/components/atomic';
import { IconButtonWrapper } from '~/components/common';
import { MEGABYTE } from '~/constants/size';
import front from '~/assets/img/generate/avarturn/front.png';
import left from '~/assets/img/generate/avarturn/left.png';
import right from '~/assets/img/generate/avarturn/right.png';
import useToast from '~/store/useToast';

interface UploadInputProps {
  file?: File;
  setFile: (s?: File) => void;
  direction: 'front' | 'left' | 'right';
}

export default function UploadInput({
  file,
  setFile,
  direction,
}: UploadInputProps) {
  const { showToast } = useToast();
  const [previewImage, setPreviewImage] = useState('');

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (file.size >= 10 * MEGABYTE) {
      showToast('error', 'cannot upload over 10MB');
      return;
    }

    if (!extension || !(extension === 'jpeg' || extension === 'jpg')) {
      showToast('error', 'Only can upload .jpg or .jpeg');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        if (typeof reader.result !== 'string') return;
        setFile(file);
        setPreviewImage(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteClick = () => {
    setFile(undefined);
  };

  if (file) {
    return (
      <ImageContainer direction={direction}>
        <img src={previewImage} />
        <DeleteButton onClick={handleDeleteClick} size={24}>
          <Icon iconName='Close' size={18} />
        </DeleteButton>
      </ImageContainer>
    );
  }

  return (
    <ImageContainer direction={direction} as={'label'}>
      <input
        type='file'
        accept='image/*'
        style={{ display: 'none' }}
        onChange={handleImageChange}
      />
      <TextWrapper>
        <Icon iconName='Add' />
        <p>{direction}</p>
      </TextWrapper>
    </ImageContainer>
  );
}

const ImageContainer = styled.div<{
  direction: UploadInputProps['direction'];
}>`
  ${({ theme, direction }) => css`
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
    border: 1px solid ${theme.stroke.stroke_1};
    border-radius: 8px;
    background: ${theme.backgroundColor.bg_light_5_alpha};

    img {
      width: 100%;
      height: 100%;
      border: 1px solid transparent;
      border-radius: 8px;
      object-fit: cover;
    }

    &::before {
      content: '';
      display: inline-block;
      position: absolute;
      bottom: 4px;
      left: 4px;
      width: 44px;
      height: 53px;
      background: url(${direction === 'front'
          ? front
          : direction === 'left'
          ? left
          : right})
        no-repeat;
    }
  `}
`;

const DeleteButton = styled(IconButtonWrapper)`
  ${({ theme }) => css`
    position: absolute;
    top: 8px;
    right: 8px;
    border-radius: 50%;
    background: ${theme.themeColor.negative};
  `}
`;

const TextWrapper = styled(FlexStack)`
  ${({ theme }) => css`
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: ${theme.content.content_light_5};
    text-transform: capitalize;
    ${theme.fontStyle.text.body14_medium};
  `}
`;
